// Thin wrapper around the shared `apiFetch` helper, hard-coding the
// `app: 'video'` flag so each call lands on the video sub-app
// (`VITE_VIDEO_API_BASE`, which CI sets to `${LIGHTNING_API_BASE}/api/video`).
//
// All media URLs (rendered MP4, all-keyframes scrub MP4s, thumbnail) are
// served as `Blob` URLs after a fetch through `apiFetch` rather than being
// handed straight to `<video src=…>` / `<img src=…>`. Why:
//
// * The SPA is served from the static-bundle CDN host; the API is the API
//   Gateway host. They are different origins.
// * `apiFetch` attaches `Authorization: Bearer <id_token>` from
//   localStorage. `<video>` / `<img>` tags can't carry that header on a
//   cross-origin request, so the gateway returns 401 and the players
//   silently render nothing.
// * Going through `apiFetch` also avoids the gateway's CORS preflight
//   issue with the `HEAD` method (the previous low-res scrub probe used
//   HEAD, which the gateway's OpenAPI spec doesn't allow).
//
// Trade-off: blob fetching downloads the entire file before playback,
// whereas a raw `<video src=URL>` streams via HTTP Range. Acceptable
// today since the scrub MP4 needs the full file in cache anyway for
// instant seeks. If the regular playback video starts feeling sluggish
// on large files we should add a backend "signed URL" endpoint instead
// of bringing back the unauthenticated path.

import { apiFetch, VIDEO_API_BASE } from '../utils/api';
import type {
  CreateScanResponse,
  HotspotsDocument,
  Job,
  PresetList,
  VideoGenConfig,
  VideoGenPreset,
  VideoGenSettings,
  VideoHealth,
} from './types';

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // body wasn't JSON; keep statusText
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

async function emptyOrThrow(res: Response): Promise<void> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
}

// ----- mutations / fetches -----------------------------------------------

export type ImageSource = 'uvpaint' | 'lightning';

export async function createScan(
  scanId: string,
  imageSource: ImageSource = 'uvpaint',
  presetId?: string,
): Promise<CreateScanResponse> {
  const res = await apiFetch('/api/scans', {
    app: 'video',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      scan_id: scanId,
      source: 'manual',
      image_source: imageSource,
      ...(presetId ? { preset_id: presetId } : {}),
    }),
  });
  return jsonOrThrow<CreateScanResponse>(res);
}

export async function listLiveJobs(): Promise<Job[]> {
  const res = await apiFetch('/api/jobs/live', { app: 'video' });
  return jsonOrThrow<Job[]>(res);
}

export async function listHistoryJobs(opts?: {
  fromDate?: Date | null;
  toDate?: Date | null;
}): Promise<Job[]> {
  const params = new URLSearchParams();
  if (opts?.fromDate) params.set('from_date', opts.fromDate.toISOString());
  if (opts?.toDate) params.set('to_date', opts.toDate.toISOString());
  const qs = params.toString();
  const res = await apiFetch(
    qs ? `/api/jobs/history?${qs}` : '/api/jobs/history',
    { app: 'video' },
  );
  return jsonOrThrow<Job[]>(res);
}

export async function fetchVideoHealth(): Promise<VideoHealth> {
  const res = await apiFetch('/health', { app: 'video' });
  return jsonOrThrow<VideoHealth>(res);
}

export async function rerunJob(jobId: string): Promise<CreateScanResponse> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/rerun`, {
    app: 'video',
    method: 'POST',
  });
  return jsonOrThrow<CreateScanResponse>(res);
}

export async function abortJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/abort`, {
    app: 'video',
    method: 'POST',
  });
  return emptyOrThrow(res);
}

export async function deleteJob(jobId: string): Promise<void> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`, {
    app: 'video',
    method: 'DELETE',
  });
  return emptyOrThrow(res);
}

export async function getVideoGenSettings(): Promise<VideoGenSettings> {
  const res = await apiFetch('/api/settings/video-gen', { app: 'video' });
  return jsonOrThrow<VideoGenSettings>(res);
}

export async function updateVideoGenSettings(config: VideoGenConfig): Promise<VideoGenConfig> {
  const res = await apiFetch('/api/settings/video-gen', {
    app: 'video',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  return jsonOrThrow<VideoGenConfig>(res);
}

export async function resetVideoGenSettings(): Promise<VideoGenConfig> {
  const res = await apiFetch('/api/settings/video-gen/reset', {
    app: 'video',
    method: 'POST',
  });
  return jsonOrThrow<VideoGenConfig>(res);
}

// ----- Presets -----------------------------------------------------------

export async function listPresets(): Promise<PresetList> {
  const res = await apiFetch('/api/settings/video-gen/presets', { app: 'video' });
  return jsonOrThrow<PresetList>(res);
}

export async function createPreset(
  name: string,
  config?: VideoGenConfig,
): Promise<VideoGenPreset> {
  const res = await apiFetch('/api/settings/video-gen/presets', {
    app: 'video',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, ...(config ? { config } : {}) }),
  });
  return jsonOrThrow<VideoGenPreset>(res);
}

export async function updatePreset(
  presetId: string,
  patch: { name?: string; config?: VideoGenConfig },
): Promise<VideoGenPreset> {
  const res = await apiFetch(`/api/settings/video-gen/presets/${encodeURIComponent(presetId)}`, {
    app: 'video',
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return jsonOrThrow<VideoGenPreset>(res);
}

export async function deletePreset(presetId: string): Promise<PresetList> {
  const res = await apiFetch(`/api/settings/video-gen/presets/${encodeURIComponent(presetId)}`, {
    app: 'video',
    method: 'DELETE',
  });
  return jsonOrThrow<PresetList>(res);
}

export async function setDefaultPreset(presetId: string): Promise<PresetList> {
  const res = await apiFetch(
    `/api/settings/video-gen/presets/${encodeURIComponent(presetId)}/default`,
    { app: 'video', method: 'POST' },
  );
  return jsonOrThrow<PresetList>(res);
}

// ----- Authenticated media fetchers --------------------------------------

/**
 * Fetch a media response and turn it into an object URL. Caller owns the
 * returned URL and is responsible for `URL.revokeObjectURL` once the
 * `<video>` / `<img>` element no longer needs it. Pass an `AbortSignal`
 * to cancel the in-flight download (e.g. when the user closes the modal
 * or scrolls a row out of view).
 */
async function fetchBlobUrl(path: string, signal?: AbortSignal): Promise<string> {
  const res = await apiFetch(path, { app: 'video', signal });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Same shape as `fetchBlobUrl` but returns `null` on 404 / 410 instead
 * of throwing. Used by callers that have a meaningful fallback (the
 * 360 viewer treats "no low-res variant" as "drive the high-res
 * element directly").
 */
async function fetchBlobUrlOrNull(
  path: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const res = await apiFetch(path, { app: 'video', signal });
  if (res.status === 404 || res.status === 410) return null;
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Inline-playback MP4. */
export function fetchVideoBlobUrl(jobId: string, signal?: AbortSignal): Promise<string> {
  return fetchBlobUrl(`/api/jobs/${encodeURIComponent(jobId)}/video`, signal);
}

/** All-keyframes companion MP4 used by the 360 viewer's drag scrub. */
export function fetchScrubVideoBlobUrl(jobId: string, signal?: AbortSignal): Promise<string> {
  return fetchBlobUrl(`/api/jobs/${encodeURIComponent(jobId)}/scrub-video`, signal);
}

/**
 * Low-resolution all-keyframes companion MP4. Cheaper to decode + seek
 * during a drag preview. Resolves to `null` for jobs from before the
 * variant existed; the viewer falls back to the full-res scrub MP4 in
 * that case (and the absence of a HEAD probe means no CORS preflight
 * grief at the gateway).
 */
export function fetchScrubVideoLowBlobUrl(
  jobId: string,
  signal?: AbortSignal,
): Promise<string | null> {
  return fetchBlobUrlOrNull(
    `/api/jobs/${encodeURIComponent(jobId)}/scrub-video-low`,
    signal,
  );
}

/** First-frame thumbnail JPEG used by the History table. */
export function fetchThumbnailBlobUrl(jobId: string, signal?: AbortSignal): Promise<string> {
  return fetchBlobUrl(`/api/jobs/${encodeURIComponent(jobId)}/thumbnail`, signal);
}

/**
 * Per-hotspot tracks for the 360 viewer's overlay. Resolves to `null`
 * when hotspots have not been computed for the job (404), so the viewer
 * degrades gracefully to a plain video with no overlay.
 */
export async function fetchHotspots(
  jobId: string,
  signal?: AbortSignal,
): Promise<HotspotsDocument | null> {
  const res = await apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/hotspots`, {
    app: 'video',
    signal,
  });
  if (res.status === 404 || res.status === 410) return null;
  return jsonOrThrow<HotspotsDocument>(res);
}

/**
 * Trigger the (decoupled) hotspot calculation for a finished job.
 *
 * `features` (default true) re-detects the blue feature hotspots via
 * Moondream; pass `false` to reuse the existing ones untouched (cheap).
 * `damages` runs the heavier red-damage path. Whichever kind isn't
 * recomputed is carried over, so "+ Damages" (features:false, damages:true)
 * does not trigger a full feature re-detect.
 */
export async function computeHotspots(
  jobId: string,
  opts?: { features?: boolean; damages?: boolean },
): Promise<HotspotsDocument> {
  const params = new URLSearchParams();
  if (opts?.features === false) params.set('features', 'false');
  if (opts?.damages) params.set('damages', 'true');
  const qs = params.toString();
  const res = await apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/hotspots/compute${qs ? `?${qs}` : ''}`,
    { app: 'video', method: 'POST' },
  );
  const body = await jsonOrThrow<{ document: HotspotsDocument }>(res);
  return body.document;
}

/**
 * Per-damage modal image (cropped original arch frame with the box drawn).
 * Fetched through `apiFetch` so the Bearer token is attached, then handed
 * to the modal as an object URL the caller must revoke.
 */
export function fetchHotspotImageBlobUrl(
  jobId: string,
  name: string,
  signal?: AbortSignal,
): Promise<string> {
  // `?v=` busts any entry poisoned by the old `max-age=86400` header; the
  // backend now sends `no-cache` so revalidation keeps the image fresh.
  return fetchBlobUrl(
    `/api/jobs/${encodeURIComponent(jobId)}/hotspots/images/${encodeURIComponent(name)}?v=2`,
    signal,
  );
}

/**
 * Trigger a browser download of the rendered MP4. Goes through
 * `apiFetch` so the Bearer token is attached (you can't put a header
 * on `<a download>`), then drives a synthetic `<a>` click against a
 * temporary blob URL.
 */
export async function downloadVideo(jobId: string, scanId: string): Promise<void> {
  const url = await fetchVideoBlobUrl(jobId);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${scanId}_full_360.mp4`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after a tick so the browser has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

// VIDEO_API_BASE is re-exported so tests + dev tools can compose the same
// URL the SPA would; runtime code should use the helpers above.
export { VIDEO_API_BASE };
